<template>
  <div class="row-editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <i class="material-icons mr-2"> alt_route </i>
        <div>{{ inputs.name || moduleConfigs.name }}</div>
      </div>
      <Tabs
        v-model="inputSectionTab"
        :tabs="tabs"
        :is-objects="true"
        @input="scrollToSection" />
    </div>

    <div
      class="editor"
      @scroll="throttleScroll">
      <div
        id="display-label"
        class="editor-section">
        <h2>Display Label</h2>
        <div class="body">
          <PivotLabelInput
            v-model="inputs.name"
            :has-pivot="!!inputs.table_pivot?.dimension"
            :pivot-label="inputs.table_pivot?.dimension?.name || ''" />

          <b-field class="mt-3">
            <b-switch
              v-model="hideLabel"
              class="mt-0"
              type="is-info"
              :disabled="!hasDisplayLabel">
              Hide label in PDF
            </b-switch>
          </b-field>
        </div>
      </div>

      <div
        id="dimension"
        class="editor-section">
        <h2>Dimension</h2>
        <div class="body">
          <PivotEditorDimensionTab
            v-model="tablePivotModel"
            :errors="childErrors('table_pivot', errors)" />
        </div>
      </div>

      <div
        id="order"
        class="editor-section">
        <h2>Order</h2>
        <div class="body">
          <OrderConfigEditor
            v-model="tablePivotModel.order"
            :errors="childErrors('order', childErrors('table_pivot', errors))" />
        </div>
      </div>

      <div
        id="filters"
        class="editor-section">
        <h2>Filters</h2>
        <div class="body">
          <FiltersTab
            v-model="tablePivotModel.filters"
            :errors="childErrors('filters', childErrors('table_pivot', errors))" />
        </div>
      </div>

      <div
        id="total-columns"
        class="editor-section">
        <h2>Total Columns</h2>
        <div class="body">
          <PivotTotalsTab
            v-model="tablePivotModel.table_totals"
            :errors="childErrors('table_totals', childErrors('table_pivot', errors))"
            :context="context" />
        </div>
      </div>

      <div
        id="header-styles"
        class="editor-section">
        <h2>Header Style</h2>
        <div class="body">
          <b-field
            class="input-field"
            label="Horizontal Alignment">
            <b-select
              v-model="inputs.header_style.h_align"
              placeholder="Select alignment"
              expanded>
              <option value="left">
                Left
              </option>
              <option value="center">
                Center
              </option>
              <option value="right">
                Right
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Tabs from '@/components/pdf_report/Tabs.vue'
  import { throttle } from 'lodash'
  import PivotEditorDimensionTab from './PivotEditorDimensionTab.vue'
  import OrderConfigEditor from '@/components/reports/config/modules/common/OrderConfigEditor'
  import PivotTotalsTab from './PivotTotalsTab.vue'
  import FiltersTab from '@/components/pdf_report/table_module/table_rows/FiltersTab'
  import PivotLabelInput from '@/components/pdf_report/PivotLabelInput.vue'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'

  export default {
    name: 'PivotGroupEditor',
    components: {
      FiltersTab,
      Tabs,
      PivotEditorDimensionTab,
      OrderConfigEditor,
      PivotTotalsTab,
      PivotLabelInput,
    },
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      errors: {
        type: Array,
        default: () => [],
      },
      context: {
        type: Object,
        default: () => ({}),
      },
    },

    data () {
      return {
        moduleConfigs: {
          name: 'Pivot Group',
          hideInLibrary: true,
        },

        inputSectionTab: 'display-label',
      }
    },

    computed: {
      tabs () {
        return [
          {
            label: 'Display Label',
            id: 'display-label',
            hasErrors: false,
          },
          {
            label: 'Dimension',
            id: 'dimension',
            hasErrors: _childErrors('dimension', _childErrors('table_pivot', this.errors)).length > 0,
          },
          {
            label: 'Order',
            id: 'order',
            hasErrors: _childErrors('order', _childErrors('table_pivot', this.errors)).length > 0,
          },
          {
            label: 'Filters',
            id: 'filters',
            hasErrors: _childErrors('filters', _childErrors('table_pivot', this.errors)).length > 0,
          },
          {
            label: 'Total Columns',
            id: 'total-columns',
            hasErrors: _childErrors('table_totals', _childErrors('table_pivot', this.errors)).length > 0,
          },
          {
            label: 'Header Styles',
            id: 'header-styles',
            hasErrors: _childErrors('header_style', this.errors).length > 0,
          },
        ]
      },
      inputs: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
      tablePivotModel: {
        get () {
          return this.inputs?.table_pivot
        },
        set (val) {
          this.inputs.table_pivot = val
        },
      },
      throttleScroll () {
        return throttle(this.onScroll, 100)
      },
      hasDisplayLabel () {
        return !!(this.inputs?.name && this.inputs.name.trim().length > 0)
      },
      hideLabel: {
        get () {
          return !(this.inputs?.label_style?.show_label ?? true)
        },
        set (val) {
          if (!this.inputs.label_style) {
            this.$set(this.inputs, 'label_style', {
              type: 'LabelStyle',
              show_label: !val,
            })
          } else {
            this.$set(this.inputs.label_style, 'show_label', !val)
          }
        },
      },
    },

    methods: {
      childErrors: _childErrors,
      onScroll (event) {
        if (this._scrollingToSection) return
        const container = event.target
        const scrollTop = container.scrollTop
        const scrollingDown = scrollTop >= (this._lastScrollTop || 0)
        this._lastScrollTop = scrollTop
        const scrollableDivRect = container.getBoundingClientRect()
        const threshold = 10
        let activeSection = this.tabs[0].id
        for (const tab of this.tabs) {
          const el = container.querySelector(`#${tab.id}`)
          if (!el) continue
          if (el.getBoundingClientRect().top <= scrollableDivRect.top + threshold) {
            activeSection = tab.id
          }
        }
        const currentIndex = this.tabs.findIndex(t => t.id === this.inputSectionTab)
        const newIndex = this.tabs.findIndex(t => t.id === activeSection)
        if (scrollingDown && newIndex >= currentIndex) {
          this.inputSectionTab = activeSection
        } else if (!scrollingDown && newIndex <= currentIndex) {
          this.inputSectionTab = activeSection
        }
      },
      scrollToSection (sectionId) {
        this._scrollingToSection = true
        this.inputSectionTab = sectionId
        const container = this.$el.querySelector('.editor')
        const el = container && container.querySelector(`#${sectionId}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', })
        }
        clearTimeout(this._scrollTimer)
        this._scrollTimer = setTimeout(() => { this._scrollingToSection = false }, 400)
      },
    },
  }
</script>

<style lang="scss" scoped>
.editor-header {
  border-bottom: $border-1;
  padding: 20px;
  background: white;

  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 18px;
    align-items: center;
    margin-bottom: 15px;
    img {
      width: 35px;
      height: 35px;
      margin-right: 10px;
      border: $border-1;
      border-radius: 8px;
      padding: 5px;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
}

.row-editor-container {
  width: 100%;
  height: 100%;
  margin: auto !important;
  background: #fbfbfb;
  display: flex;
  flex-direction: column;
  padding: 0px;
}

.editor {
  padding: 20px;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: auto;
  flex-direction: column;

  .editor-section {
    display: flex;
    flex-direction: column;
    margin-bottom: 60px;

    h2 {
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 10px;
      text-transform: capitalize;
    }

    .body {
      border: $border-1;
      border-radius: 10px;
      padding: 18px;
      background: white;
    }
  }
}
</style>
